import { ProblemData } from "./Constants.ts";

/**
 * A fact key ("7x8", "56÷8", "6+7", "13-6") and the problem it stands for.
 *
 * Two things build sheets from known facts rather than drawing them: practice
 * from missed facts (ADR 0012) and fact fluency levels (ADR 0071). Both parse
 * through here, so a key means the same problem to each. Its own module so the
 * level generator can use it without importing ProblemGenerator.tsx, which
 * imports the level generator.
 */

const SIGN_TO_ANSWER: Record<string, (a: number, b: number) => number> = {
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  x: (a, b) => a * b,
  '÷': (a, b) => (b === 0 ? 0 : a / b),
};

/**
 * Parses a fact key back into a ProblemData, or null if it will not. The
 * numbers are laid out as the vertical renderer reads them - [top, bottom,
 * answer] - which for division is [dividend, divisor, quotient], the same
 * order `buildDivision` produces.
 */
export function problemFromFactKey(key: string, index: number): ProblemData | null {
  const match = key.match(/^(\d+)\s*([+\-x÷])\s*(\d+)$/);
  if (!match) {
    return null;
  }
  const [, left, sign, right] = match;
  const a = parseInt(left, 10);
  const b = parseInt(right, 10);
  const answer = SIGN_TO_ANSWER[sign](a, b);
  if (!Number.isFinite(answer)) {
    return null;
  }
  return { key: index, numbers: [a, b, answer], operator: sign };
}
